import React, { useContext, useState } from 'react';

import styled from 'styled-components';
import { NavLink as RouterLink } from 'react-router-dom';
import Fade from '@material-ui/core/Fade';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChevronDoubleLeft,
	faChevronDoubleRight
} from '@fortawesome/pro-regular-svg-icons';

import { breakpoints } from '@sorted/sorted-ui';
import CollapsedLogo from './assets/sorted-logo.svg';
import SortedWordLogo from './assets/sorted-text.svg';

import UserContext from '../../context/UserContext';
import Copyright from './Copyright';
import Logout from './Logout';
import AccountMenu from '../MenuFragments/Account';
import colors from './colors';
import SettingsMenu from './SettingsMenu';
import { sizing } from '../../shared/globalStyles';
import ProMenu from '../MenuFragments/Pro';
import ReactMenu from '../MenuFragments/React';
import MicroFrontendContext from '../../context/MicroFrontendContext';

const getInitialState = () => {
	const persistedValue = localStorage.getItem(
		'sorted-ui-host.sidebar.expanded'
	);
	return persistedValue === null ? true : persistedValue === 'true';
};

const { sideBarExpanded, sideBarCollapsed } = sizing;

const Navigator = styled.nav`
	display: none;

	@media (min-width: ${breakpoints.smallDesktop}) {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		transition: all 0.1s ease;
		z-index: 1000;
		color: white;
		background-color: ${colors.navBg};

		flex: 0 0
			${({ expanded }) => (expanded ? sideBarExpanded : sideBarCollapsed)};
		width: ${({ expanded }) =>
			expanded ? sideBarExpanded : sideBarCollapsed};
	}
`;

const LogoArea = styled(RouterLink)`
	display: flex;
	align-items: flex-end;
	overflow: hidden;
	padding: 24px 22px 15px 25px;

	svg {
		fill: white;
		margin-top: auto;

		&:first-child {
			margin-right: 5px;
			flex-shrink: 0;
		}
	}
`;

const TopItems = styled.div`
	flex: 1 1 100%;
	display: flex;
	flex-direction: column;
	will-change: transform;
	transition: transform 0.2s ease;

	&::after {
		content: '';
		background: ${colors.navBg};
		box-sizing: border-box;
	}
`;

const BottomItems = styled.div`
	flex: 0 0 32px;
	display: block;
	z-index: 999;
`;

const ExpandCollapse = styled.button`
	width: 100%;
	height: 32px;
	color: ${colors.white};
	border: none;
	background-color: ${colors.logoBg};
	font-size: 18px;
	font-weight: bold;
	outline: 0;

	&:hover {
		background-color: ${colors.black};
	}
`;

const DesktopLogo = styled.div`
	display: flex;
`;

// icon render helper
const renderMenuIcon = (icon, size = 'lg') => (
	<FontAwesomeIcon
		style={{ pointerEvents: 'none' }}
		size={size}
		icon={icon}
		fixedWidth
	/>
);

// const reactClaim = 'REACT';
const proClaim = 'PRO';
const reactMenu = 'REACT';
const proMenu = 'PRO';
const settingsMenu = 'Settings';
// const docsMenu = 'Help & Support';
const accountMenu = 'Account';

const Sidebar = () => {
	const [menuExpanded, setMenuExpanded] = useState(getInitialState());
	const [openMenu, setOpenMenu] = useState('');

	const { user } = useContext(MicroFrontendContext);

	const {
		user: { productClaims }
	} = useContext(UserContext);

	const hasProductClaims = requiredClaims =>
		requiredClaims.some(claim => productClaims.includes(claim));

	const onToggleMenuWidth = () => {
		const newState = !menuExpanded;
		setMenuExpanded(newState);
		localStorage.setItem('sorted-ui-host.sidebar.expanded', newState);
	};

	const onOpenMenu = name => {
		setOpenMenu(openMenu === name ? '' : name);
	};

	return (
		<>
			<Navigator expanded={menuExpanded}>
				<LogoArea to="/">
					<DesktopLogo>
						<CollapsedLogo width={28} height={49} />
						<Fade in={menuExpanded}>
							<SortedWordLogo />
						</Fade>
					</DesktopLogo>
				</LogoArea>

				<TopItems role="region">
					<ReactMenu
						isOpen={openMenu === reactMenu}
						onShowHide={() => onOpenMenu(reactMenu)}
						sidebarCollapsed={!menuExpanded}
						productClaims={productClaims}
					/>

					<ProMenu
						isOpen={openMenu === proMenu}
						onShowHide={() => onOpenMenu(proMenu)}
						sidebarCollapsed={!menuExpanded}
						productClaims={productClaims}
					/>

					{!user.roles.readOnly && (
						<SettingsMenu
							isOpen={openMenu === settingsMenu}
							onShowHide={() => onOpenMenu(settingsMenu)}
							sidebarCollapsed={!menuExpanded}
							isProUser={hasProductClaims([proClaim])}
						/>
					)}

					{/* <HelpMenu
						isOpen={openMenu === docsMenu}
						onShowHide={() => onOpenMenu(docsMenu)}
						sidebarCollapsed={!menuExpanded}
						productClaims={productClaims}
					/> */}
				</TopItems>

				<BottomItems>
					<AccountMenu
						title={accountMenu}
						onClick={() => onOpenMenu(accountMenu)}
						isOpen={openMenu === accountMenu}
						isCollapsed={!menuExpanded}
						productClaims={productClaims}
					/>

					<Logout isOpen={menuExpanded} />

					<Copyright isCollapsed={!menuExpanded} originYear={2020} />

					<ExpandCollapse
						aria-label="Expand/Collapse Menu"
						onClick={onToggleMenuWidth}
					>
						{menuExpanded
							? renderMenuIcon(faChevronDoubleLeft, 'xs')
							: renderMenuIcon(faChevronDoubleRight, 'xs')}
					</ExpandCollapse>
				</BottomItems>
			</Navigator>
		</>
	);
};

export default Sidebar;
